import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ContractFormType, ContractingEntityErrorFlag, ContractStatusRequest, StateTree } from '@makemydeal/dr-dash-types';
import { ContractingOperation } from '@makemydeal/dr-dash-types';
import type { GenericObject, OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { RSAA } from 'redux-api-middleware';
import { getHasDeskingPermission } from '../selectors/customerSpdSelectors';

import { nanoid } from 'nanoid';
import {
    CONTRACTING_CLOSE_MODAL,
    CONTRACTING_EVENT_RECEIVED,
    CONTRACTING_GENERATE_CLICKED,
    CONTRACTING_IN_PROGRESS,
    CONTRACTING_OPEN_MODAL,
    CONTRACTING_STAGE_CANCEL_FAILURE,
    CONTRACTING_STAGE_CANCEL_REQUEST,
    CONTRACTING_STAGE_CANCEL_SUCCESS,
    CONTRACTING_STAGE_COPY_REQUEST,
    CONTRACTING_STAGE_START_FAILURE,
    CONTRACTING_STAGE_START_REQUEST,
    CONTRACTING_STAGE_START_SUCCESS,
    COPY_CREDIT_APP_DEAL_UPDATE_FAILURE,
    COPY_CREDIT_APP_DEAL_UPDATE_REQUEST,
    COPY_CREDIT_APP_DEAL_UPDATE_SUCCESS,
    COPY_CREDIT_APP_HTTP_ACK,
    COPY_CREDIT_APP_HTTP_FAILURE,
    CONTRACTING_SET_FORM_CONTRACT_TYPE,
    CONTRACTING_STAGE_VERIFY_FAILURE,
    CONTRACTING_STAGE_VERIFY_REQUEST,
    CONTRACTING_STAGE_VERIFY_SUCCESS,
    CONTRACTING_STATUS_FAILURE,
    CONTRACTING_STATUS_REQUEST,
    CONTRACTING_STATUS_SUCCESS,
    SET_CONTRACT_VALIDATION_ERRORS,
    SET_CONTRACT_VALIDATION_LOADING,
    SET_FIRST_CONTRACT_API_ERRORED_VPP
} from '../actionTypes/contractingActionTypes';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getPartnerCode, getPartnerDealerId } from '../selectors/calculationDetailsSelectors';
import {
    getContractApiErroredVppFieldFlags,
    getContractFormType,
    getContractingCorrelationId,
    getContractErrorFlags,
    getIsContractingContext
} from '../selectors/contractingSelectors';
import { getDealXgVersion } from '../selectors/offerInfoSelectors';
import { getCreditDecisionLender, getCurrentOfferType, getDealXgId, getSessionId } from '../selectors/offerRedux';
import { getCommonOrgId, getDealRefId } from '../selectors/offerSelectors';
import { hasAllSpdData, runContractValidations } from '../utils/contractValidationRunner';
import { flattenNestedErrors } from '../utils/contractingUtils';
import { clearCustomerSpdError, customerSpdRSAA } from './CustomerSpdActionCreators';
import {
    buildContractingQueryString,
    BUYER,
    CO_BUYER,
    DealTabs,
    VEHICLE_INSURANCE,
    VEHICLE_ENTITY,
    GAP,
    CONTRACT_API_ERRORED_VPP,
    CONTRACT_API_ERRORED_VEHICLE_INSURANCE,
    TRADE_IN,
    TRADE_INS,
    type DealProduct
} from '@makemydeal/dr-shared-types';
import type { ValidationErrorState } from '@makemydeal/dr-shared-ui-utils';
import { setErrorFields, setErrorFieldsByCategory } from './dealScreenErrorActionCreators';
import { ACCESSORIES } from '../constants';
import {
    buildContractApiErroredVppErrors,
    buildGapContractErrors,
    VPP_CONTRACT_API_ERRORS_KEY,
    VPP_GAP_CONTRACT_ERRORS_KEY
} from '../utils/vppProductErrors';
import {
    getSelectedGapProductsForContract,
    getContractApiErroredVPPs,
    getSelectedDealProducts,
    isContractApiErroredVppProduct
} from '../selectors/vehicleProtectionSelectors';
import type { FieldErrors } from '../reducers/dealScreenErrorReducer';

const CASH_LENDER_ID = 'DTH';
const TARGET_PLATFORM_ID = 'DTC';
const DEFAULT_PARTNER_CODE = 'MMD';

export const contractingEventReceived = (event: any): AnyFSA => {
    return {
        type: CONTRACTING_EVENT_RECEIVED,
        payload: event.data
    };
};

export const contractingOpenModal = (): AnyFSA => {
    return {
        type: CONTRACTING_OPEN_MODAL
    };
};

export const contractingCloseModal = (success?: boolean): AnyFSA => {
    const action: AnyFSA = {
        type: CONTRACTING_CLOSE_MODAL
    };
    if (success != null) {
        action.meta = { success };
    }
    return action;
};

export const contractingStartProgress = (): AnyFSA => {
    return {
        type: CONTRACTING_IN_PROGRESS
    };
};

export const contractingGenerateClicked = (): AnyFSA => {
    return {
        type: CONTRACTING_GENERATE_CLICKED
    };
};

export const setContractFormType = (formContractType: ContractFormType): AnyFSA => {
    return {
        type: CONTRACTING_SET_FORM_CONTRACT_TYPE,
        payload: { formContractType }
    };
};

export type ContractingCommonOptions = {
    sourcePartnerId: string;
    dealRefId: string;
    dealerId: number;
    /** targetPlatforms.partyId */
    partyId: string;
    /** RSAA action types */
    types: [request: string, success: string, failure: string];
    correlationId: string;
};

export type ContractingCancelOptions = ContractingCommonOptions;

export type ContractingStartOptions = ContractingCommonOptions & {
    dealXgDealId: string;
    dealXgDealVersion: string;
    /**
     * offerType
     */
    financeMethod: string;
    lenderId?: string;
};

export type ContractingVerifyOptions = ContractingCommonOptions & {
    lenderId?: string;
};

export type ContractingSendCopyCreditAppPayload = {
    correlationId: string;
    dealRefId: string;
    sourceCreditAppId: string;
    sourcePartnerId: string;
    targetPlatformId: string;
    partyId: string;
    dealerId: string;
    dealXgDealId: string;
};

export type ContractingSendCopyCreditAppHeaders = {
    partnerAuth?: string;
};

export type UpdateDealForCopyCreditAppPayload = {
    correlationId: string;
    dealRefId: string;
    dealXgDealId: string;
    dealXgDealVersion: string;
    newCreditAppId: string;
    dealerId: string;
};

export const getContractingPartyId = (state: StateTree) => {
    return String(dealerSelectors.getPartnerDealerId(state) || getPartnerDealerId(state) || dealerSelectors.getDealerId(state));
};

export const getContractingSourcePartnerId = (state: StateTree) => {
    return dealerSelectors.getPartnerId(state) || getPartnerCode(state) || DEFAULT_PARTNER_CODE;
};

export const setContractValidationErrors = (errors: ValidationErrorState) => ({
    type: SET_CONTRACT_VALIDATION_ERRORS,
    payload: errors
});

export const setFirstContractApiErroredVPP = (product: DealProduct | undefined): AnyFSA => ({
    type: SET_FIRST_CONTRACT_API_ERRORED_VPP,
    payload: product
});

export const getLenderId = (state: GenericObject, offerType: OfferType): string | undefined => {
    return offerType === CASH ? CASH_LENDER_ID : getCreditDecisionLender(state)?.id;
};

export const flattenTradeInErrors = (tradeInErrors: Record<string, any>, isIndexed: boolean): ContractingEntityErrorFlag[] => {
    const result: ContractingEntityErrorFlag[] = [];

    if (isIndexed) {
        Object.keys(tradeInErrors).forEach((indexKey) => {
            result[Number(indexKey)] = flattenNestedErrors(tradeInErrors[indexKey]) as unknown as ContractingEntityErrorFlag;
        });
    } else {
        result[0] = flattenNestedErrors(tradeInErrors) as unknown as ContractingEntityErrorFlag;
    }

    return result;
};

export const syncRedDotErrors = (dispatch: any, getState: any) => {
    const state = getState();
    const isGCDynamicValidationsEnabled = featureToggleSelectors.isGCDynamicValidationsEnabled(state);
    if (isGCDynamicValidationsEnabled) {
        const entityErrors = getContractErrorFlags(state);
        const skipAccumulation = true;
        // Pass skipAccumulation: true only for LENDER and PROTECTION tabs (they have error accumulation logic)
        dispatch(setErrorFields(BUYER, entityErrors[BUYER]));
        dispatch(setErrorFields(CO_BUYER, entityErrors[CO_BUYER]));
        const lenderErrors = entityErrors[DealTabs.LENDER] ?? { name: false };
        dispatch(setErrorFields(DealTabs.LENDER, lenderErrors, skipAccumulation));

        // set false for Vehicle Insurance (VI) fields when no errors identified for VI on regenerate contract button click.
        const vehicleInsuranceFieldErrors = entityErrors[VEHICLE_INSURANCE]
            ? entityErrors[VEHICLE_INSURANCE]
            : { liabilityCoverage: false, policyNumber: false, policyExpirationDate: false };

        // Contract-API errored fields (missing required lender fields) merge onto the same VI payload.
        const contractApiErroredVehicleInsuranceErrors = entityErrors[CONTRACT_API_ERRORED_VEHICLE_INSURANCE]
            ? (entityErrors[CONTRACT_API_ERRORED_VEHICLE_INSURANCE] as Record<string, boolean>)
            : {
                  apiInsuranceCompany: false,
                  apiAgentPhoneNumber: false,
                  apiPolicyNumber: false,
                  apiPolicyStartDate: false,
                  apiPolicyExpirationDate: false
              };

        dispatch(
            setErrorFields(VEHICLE_INSURANCE, { ...vehicleInsuranceFieldErrors, ...contractApiErroredVehicleInsuranceErrors })
        );

        // Re-key onto product identity and replace only this flow's own map.
        const gapContractErrors = buildGapContractErrors(
            entityErrors[GAP] as Record<string, FieldErrors> | undefined,
            getSelectedGapProductsForContract(state)
        );
        const contractApiErroredVppErrors = buildContractApiErroredVppErrors(
            entityErrors[CONTRACT_API_ERRORED_VPP] as Record<string, FieldErrors> | undefined,
            getContractApiErroredVPPs(state)
        );
        dispatch(
            setErrorFields(
                DealTabs.PROTECTION,
                {
                    [VPP_GAP_CONTRACT_ERRORS_KEY]: gapContractErrors,
                    [VPP_CONTRACT_API_ERRORS_KEY]: contractApiErroredVppErrors
                } as unknown as Record<string, boolean>,
                skipAccumulation
            )
        );
        dispatch(setErrorFields(VEHICLE_ENTITY, entityErrors[VEHICLE_ENTITY]));
        const tradeInsErrors = entityErrors[TRADE_INS];
        const tradeInErrors = entityErrors[TRADE_IN];
        const resolvedTradeInErrors = tradeInsErrors || tradeInErrors;
        if (resolvedTradeInErrors) {
            const isIndexed = Boolean(tradeInsErrors);
            const tradeInErrorsArray = flattenTradeInErrors(resolvedTradeInErrors, isIndexed);
            const skipCategoryMerge = false;
            dispatch(
                setErrorFieldsByCategory(
                    DealTabs.TRADE_IN,
                    DealTabs.TRADE_IN,
                    tradeInErrorsArray,
                    skipCategoryMerge,
                    -1,
                    skipAccumulation
                )
            );
        }

        const accessoryErrors = entityErrors[DealTabs.ACCESSORIES];
        if (accessoryErrors) {
            const accessoryErrorsArray = Object.keys(accessoryErrors).reduce((acc, key) => {
                acc[Number(key)] = accessoryErrors[key];
                return acc;
            }, [] as ContractingEntityErrorFlag[]);
            const skipCategoryMerge = false;
            dispatch(
                setErrorFieldsByCategory(
                    DealTabs.ACCESSORIES,
                    ACCESSORIES,
                    accessoryErrorsArray,
                    skipCategoryMerge,
                    -1,
                    skipAccumulation
                )
            );
        }
    }
};

/**
 * Re-runs contract validations and refreshes the stored GC Alert error snapshot in the background.
 *
 * This enables GC Alert errors to disappear/update after the user commits changes to Redux
 * (Apply/Save/Update or screen soft-save), without requiring the user to explicitly regenerate
 * the contract or re-open the Generate Contract workflow.
 *
 * Guarded so we only refresh after a validation snapshot exists (i.e., after Generate Contract
 * has run at least once and produced/stored validation output).
 *
 * Note: we intentionally do NOT "revalidate a single entity" here. Validation rules often depend
 * on values from other entities (e.g., lender selection impacting protection rules, buyer/co-buyer
 * address impacting tax/fees, etc). Revalidating the entire deal state avoids stale errors and keeps
 * red-dot flags consistent with the current Redux state.
 */
export const refreshContractValidationErrors = () => (dispatch: any, getState: any) => {
    const state: StateTree = getState();
    if (!getIsContractingContext(state)) {
        return;
    }

    const { errors } = runContractValidations(state);
    dispatch(setContractValidationErrors(errors));
    // Keep red-dot store in sync with the refreshed snapshot.
    syncRedDotErrors(dispatch, getState);
};

export const revalidateContractAfterApiFailure = () => (dispatch: any, getState: any) => {
    const state: StateTree = getState();
    if (!featureToggleSelectors.isGCDynamicValidationsEnabled(state)) {
        return;
    }
    const fieldFlags = getContractApiErroredVppFieldFlags(state);
    const firstContractApiErroredVPP = getSelectedDealProducts(state).find((p) => isContractApiErroredVppProduct(p, fieldFlags));
    dispatch(setFirstContractApiErroredVPP(firstContractApiErroredVPP));
    dispatch(validateContractFields());
};

export const validateContractFields = () => (dispatch: any, getState: any) => {
    const state = getState();
    const isSpdManagementEnabled = dealerSelectors.isSpdManagementEnabled(state);
    const { hasBuyerData, hasCoBuyerData } = hasAllSpdData(state);

    const isAuthorized = getHasDeskingPermission(state);

    if (isSpdManagementEnabled && (!hasBuyerData || !hasCoBuyerData) && isAuthorized) {
        dispatch(setContractValidationLoading(true));
        dispatch(clearCustomerSpdError());
        if (!hasBuyerData) {
            const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
            dispatch(customerSpdRSAA(buyerCommonConsumerId));
        }
        if (!hasCoBuyerData) {
            const coBuyerCommonConsumerId = offerReduxSelectors.getCoBuyerCommonConsumerId(state);
            dispatch(customerSpdRSAA(coBuyerCommonConsumerId));
        }
        return;
    }

    dispatch(runContractValidationFlow());
};

export const runContractValidationFlow = () => (dispatch: any, getState: any) => {
    const state = getState();
    const { errors, hasErrors } = runContractValidations(state);

    dispatch(setContractValidationLoading(false));
    if (hasErrors) {
        dispatch(setContractValidationErrors(errors));
    }
    syncRedDotErrors(dispatch, getState); // Sync red dot errors for both error / non-error condition to clear the contract api errors
    if (!hasErrors) dispatch(contractingOpenModal());
};

export const setContractValidationLoading = (isLoading: boolean): AnyFSA => {
    return {
        type: SET_CONTRACT_VALIDATION_LOADING,
        payload: { isLoading }
    };
};

export const contractingSendStart = () => (dispatch: any, getState: any) => {
    const state = getState();
    const financeMethod = getCurrentOfferType(state);
    const dealXgDealId = getDealXgId(state);
    const dealXgDealVersion = getDealXgVersion(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const offerType = getCurrentOfferType(state);
    const lenderId = getLenderId(state, offerType);
    const dealRefId = getDealRefId(state);
    const correlationId = getContractingCorrelationId(state);
    const partyId = getContractingPartyId(state);
    const sourcePartnerId = getContractingSourcePartnerId(state);
    const formContractType = getContractFormType(state);

    const locHref = locHrefUtil.getLocHref();
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/contracting');

    const body = {
        operation: ContractingOperation.Start,
        sourcePartnerId,
        id: TARGET_PLATFORM_ID,
        partyId,
        dealerId,
        dealRefId,
        dealXgDealId,
        dealXgDealVersion,
        financeMethod,
        lenderId
    };

    dispatch({
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'x-coxauto-correlation-id': correlationId,
                ...(formContractType && { 'form-contract-type': formContractType })
            },
            body: JSON.stringify(body),
            types: [CONTRACTING_STAGE_START_REQUEST, CONTRACTING_STAGE_START_SUCCESS, CONTRACTING_STAGE_START_FAILURE]
        }
    });
};

export const contractingSendVerify = () => (dispatch: any, getState: any) => {
    const state = getState();
    const offerType = getCurrentOfferType(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const lenderId = getLenderId(state, offerType);
    const partyId = getContractingPartyId(state);
    const sourcePartnerId = getContractingSourcePartnerId(state);
    const correlationId = getContractingCorrelationId(state);
    const dealRefId = getDealRefId(state);
    const dealXgDealId = getDealXgId(state);
    const dealXgDealVersion = getDealXgVersion(state);
    const formContractType = getContractFormType(state);

    const locHref = locHrefUtil.getLocHref();
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/contracting');

    const body = {
        operation: ContractingOperation.Verify,
        sourcePartnerId,
        id: TARGET_PLATFORM_ID,
        partyId,
        dealerId,
        dealRefId,
        lenderId,
        dealXgDealId,
        dealXgDealVersion
    };

    dispatch({
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'x-coxauto-correlation-id': correlationId,
                ...(formContractType && { 'form-contract-type': formContractType })
            },
            body: JSON.stringify(body),
            types: [CONTRACTING_STAGE_VERIFY_REQUEST, CONTRACTING_STAGE_VERIFY_SUCCESS, CONTRACTING_STAGE_VERIFY_FAILURE]
        }
    });
};

export const contractingSendCopyCreditApp = (
    payload: ContractingSendCopyCreditAppPayload,
    headers?: ContractingSendCopyCreditAppHeaders
) => {
    const locHref = locHrefUtil.getLocHref();
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/copy-credit-application');

    const httpHeaders: Record<string, string> = {
        'content-type': 'application/json',
        'x-coxauto-correlation-id': payload.correlationId
    };
    if (headers?.partnerAuth) {
        httpHeaders['x-partner-auth'] = headers.partnerAuth;
    }

    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: httpHeaders,
            body: JSON.stringify(payload),
            types: [CONTRACTING_STAGE_COPY_REQUEST, COPY_CREDIT_APP_HTTP_ACK, COPY_CREDIT_APP_HTTP_FAILURE]
        }
    };
};

/** SUCCESS and FAILURE both bridge to STAGE_COPY_SUCCESS — failures are graceful-degrade (NR alert; FE proceeds). */
export const updateDealForCopyCreditApp = (payload: UpdateDealForCopyCreditAppPayload) => {
    const locHref = locHrefUtil.getLocHref();
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(
        MANAGER_VIEW_APP_PREFIX,
        locHref,
        '/copy-credit-application/update-deal'
    );

    return {
        [RSAA]: {
            endpoint,
            method: 'PATCH',
            headers: {
                'content-type': 'application/json',
                'x-coxauto-correlation-id': payload.correlationId
            },
            body: JSON.stringify(payload),
            types: [COPY_CREDIT_APP_DEAL_UPDATE_REQUEST, COPY_CREDIT_APP_DEAL_UPDATE_SUCCESS, COPY_CREDIT_APP_DEAL_UPDATE_FAILURE]
        }
    };
};

export const contractingSendCancel = () => (dispatch: any, getState: any) => {
    const state = getState();
    const sourcePartnerId = getContractingSourcePartnerId(state);
    const dealRefId = getDealRefId(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const partyId = getContractingPartyId(state);
    // for workflow v2, need to generate a new correlation id so we don't act on ghost events from the previous cycle
    const correlationId = nanoid();
    const dealXgDealId = getDealXgId(state);
    const dealXgDealVersion = getDealXgVersion(state);

    const locHref = locHrefUtil.getLocHref();
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/contracting');

    const body = {
        operation: ContractingOperation.Cancel,
        sourcePartnerId,
        id: TARGET_PLATFORM_ID,
        partyId,
        dealerId,
        dealRefId,
        dealXgDealId,
        dealXgDealVersion
    };

    dispatch({
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'x-coxauto-correlation-id': correlationId
            },
            body: JSON.stringify(body),
            types: [
                {
                    type: CONTRACTING_STAGE_CANCEL_REQUEST,
                    meta: { correlationId }
                },
                CONTRACTING_STAGE_CANCEL_SUCCESS,
                CONTRACTING_STAGE_CANCEL_FAILURE
            ]
        }
    });
};

export const getContractingStatus = () => (dispatch: any, getState: any) => {
    const state = getState();

    const offerType = getCurrentOfferType(state);

    const partyId = getContractingPartyId(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const lenderId = getLenderId(state, offerType);
    const dealRefId = getDealRefId(state);
    const sessionId = getSessionId(state);
    const commonOrgId = getCommonOrgId(state);
    const dealXgDealId = getDealXgId(state);
    const financeMethod = getCurrentOfferType(state);
    const correlationId = getContractingCorrelationId(state);
    const sourcePartnerId = getContractingSourcePartnerId(state);
    const dealXgDealVersion = getDealXgVersion(state);

    const locHref = locHrefUtil.getLocHref();
    const query: ContractStatusRequest = {
        commonOrgId: commonOrgId,
        targetPlatformId: TARGET_PLATFORM_ID
    };
    const queryString = buildContractingQueryString(query);
    const { url: endpoint } = apiConfigHelper.buildBffApiUrl(
        MANAGER_VIEW_APP_PREFIX,
        locHref,
        `/contracting/status/${dealRefId}?${queryString}`
    );

    const headersData = {
        partyId,
        dealerId,
        lenderId,
        dealRefId,
        sessionId,
        dealXgDealId,
        financeMethod,
        sourcePartnerId,
        dealXgDealVersion,
        'x-coxauto-correlation-id': correlationId
    };
    const statusHeaders = Object.fromEntries(Object.entries(headersData).filter(([, value]) => value != null));

    dispatch({
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: {
                'content-type': 'application/json',
                ...statusHeaders
            },
            types: [CONTRACTING_STATUS_REQUEST, CONTRACTING_STATUS_SUCCESS, CONTRACTING_STATUS_FAILURE]
        }
    });
};
